/**
 * Report Issue Modal - For white-label clients to report problems
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Send, CheckCircle, Monitor, Smartphone, Globe, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ISSUE_TYPES = [
  { value: 'bug', label: 'Something is broken' },
  { value: 'feature', label: 'Feature request' },
  { value: 'question', label: 'Question / How do I...' },
  { value: 'billing', label: 'Billing issue' },
  { value: 'other', label: 'Other' }
];

export const ReportIssueModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    type: 'bug',
    subject: '',
    description: '',
    steps: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Gather browser/system info automatically
  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let device = 'Desktop';
    
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    
    if (/Mobi|Android/i.test(ua)) device = 'Mobile';
    else if (/Tablet|iPad/i.test(ua)) device = 'Tablet';
    
    return {
      browser,
      device,
      os: navigator.platform,
      screen: `${window.screen.width}x${window.screen.height}`,
      url: window.location.href,
      timestamp: new Date().toISOString()
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in subject and description');
      return;
    }

    setSubmitting(true);
    
    try {
      const systemInfo = getBrowserInfo();
      
      await axios.post(`${API}/api/support/report-issue`, {
        ...formData,
        system_info: systemInfo
      });
      
      setSubmitted(true);
      toast.success('Issue reported! We\'ll get back to you soon.');
      
      // Reset and close after delay
      setTimeout(() => {
        setFormData({ type: 'bug', subject: '', description: '', steps: '', email: '' });
        setSubmitted(false);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Failed to submit issue:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Report an Issue</h2>
                <p className="text-sm text-muted-foreground">We'll help you resolve it</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-full transition-colors"
              data-testid="close-report-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          {submitted ? (
            <div className="p-12 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle className="w-8 h-8 text-green-600" />
              </motion.div>
              <h3 className="text-xl font-semibold mb-2">Issue Reported!</h3>
              <p className="text-muted-foreground">We'll review it and get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Issue Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Issue Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="issue-type-select"
                >
                  {ISSUE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium mb-2">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of the issue"
                  className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  data-testid="issue-subject"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What happened? What did you expect to happen?"
                  rows={4}
                  className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  required
                  data-testid="issue-description"
                />
              </div>

              {/* Steps to Reproduce */}
              <div>
                <label className="block text-sm font-medium mb-2">Steps to Reproduce (optional)</label>
                <textarea
                  value={formData.steps}
                  onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                  rows={3}
                  className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  data-testid="issue-steps"
                />
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium mb-2">Your Email (for follow-up)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="issue-email"
                />
              </div>

              {/* System Info Preview */}
              <div className="p-3 bg-secondary/50 rounded-xl">
                <p className="text-xs text-muted-foreground mb-2">System info (auto-captured):</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="flex items-center gap-1 px-2 py-1 bg-secondary rounded-full">
                    <Globe className="w-3 h-3" /> {navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                      navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                      navigator.userAgent.includes('Safari') ? 'Safari' : 'Browser'}
                  </span>
                  <span className="flex items-center gap-1 px-2 py-1 bg-secondary rounded-full">
                    {/Mobi|Android/i.test(navigator.userAgent) ? 
                      <><Smartphone className="w-3 h-3" /> Mobile</> : 
                      <><Monitor className="w-3 h-3" /> Desktop</>}
                  </span>
                  <span className="flex items-center gap-1 px-2 py-1 bg-secondary rounded-full">
                    {window.screen.width}x{window.screen.height}
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-issue"
              >
                {submitting ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> Submitting...</>
                ) : (
                  <><Send className="w-4 h-4" /> Submit Issue</>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReportIssueModal;
